import { Building2, RefreshCw, AlertTriangle, Plus, Shield } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import type { EnrichedAccount } from '@/stores/connectionStore';

interface AccountSelectorProps {
  accounts: EnrichedAccount[];
  isLoading: boolean;
  onSelect: (accountId: string) => void;
  onConnect: () => void;
  onRefresh: () => void;
}

function getInitials(name?: string | null, email?: string | null) {
  if (name) {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }
  return email?.charAt(0).toUpperCase() ?? 'U';
}

function Badge({ children, variant = 'default' }: {
  children: React.ReactNode;
  variant?: 'default' | 'success' | 'warning' | 'muted';
}) {
  const styles = {
    default: 'bg-primary/10 text-primary border-primary/20',
    success: 'bg-green-500/10 text-green-400 border-green-500/20',
    warning: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
    muted: 'bg-secondary text-muted-foreground border-border',
  };

  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${styles[variant]}`}>
      {children}
    </span>
  );
}

function SkeletonCard() {
  return (
    <div className="animate-pulse rounded-xl border border-border bg-card p-5">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-lg bg-secondary" />
        <div className="flex-1 space-y-2">
          <div className="h-4 w-36 rounded bg-secondary" />
          <div className="h-3 w-24 rounded bg-secondary" />
        </div>
      </div>
      <div className="mt-4 flex gap-2">
        <div className="h-5 w-16 rounded-full bg-secondary" />
        <div className="h-5 w-14 rounded-full bg-secondary" />
        <div className="h-5 w-12 rounded-full bg-secondary" />
      </div>
      <div className="mt-4 h-3 w-44 rounded bg-secondary" />
    </div>
  );
}

function OrgCard({ account, onSelect }: { account: EnrichedAccount; onSelect: (id: string) => void }) {
  const isExpired = account.enrichmentStatus === 'token_expired';
  const isError = account.enrichmentStatus === 'error';
  const hasWarning = isExpired || isError;

  const orgName = account.accountName || 'Zoom Account';
  const connectedDate = new Date(account.connectedAt).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  return (
    <button
      onClick={() => onSelect(account.accountId)}
      className="group w-full rounded-xl border border-border bg-card p-5 text-left transition-all hover:border-primary/50 hover:bg-secondary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >
      {/* Org icon + name */}
      <div className="flex items-start gap-3">
        <div className="relative shrink-0">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
            <Building2 className="h-5 w-5 text-primary" />
          </div>
          <div className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-card ${
            hasWarning ? 'bg-yellow-500' : 'bg-green-500'
          }`} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-foreground">{orgName}</p>
          {account.email && (
            <p className="mt-0.5 truncate text-xs text-muted-foreground">{account.email}</p>
          )}
        </div>
      </div>

      {/* Warning for expired/error tokens */}
      {hasWarning && (
        <div className="mt-3 flex items-center gap-1.5 rounded-lg border border-yellow-500/20 bg-yellow-500/10 px-2.5 py-1.5">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-yellow-400" />
          <span className="text-xs text-yellow-400">
            {isExpired ? 'Token expired \u2014 reconnect to refresh' : 'Connection issue'}
          </span>
        </div>
      )}

      {/* Badges */}
      <div className="mt-3 flex flex-wrap gap-1.5">
        {account.enrichmentStatus === 'ok' && account.userTypeName && (
          <Badge variant="default">{account.userTypeName}</Badge>
        )}
        {account.enrichmentStatus === 'ok' && account.roleName && (
          <Badge variant="muted">{account.roleName}</Badge>
        )}
        <Badge variant="muted">{account.authType === 'oauth' ? 'OAuth' : 'S2S'}</Badge>
      </div>

      {/* Connection info */}
      <p className="mt-3 text-xs text-muted-foreground">
        Connected {connectedDate}
        {account.lastUsedAt && (
          <> &middot; Last used {new Date(account.lastUsedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</>
        )}
      </p>
    </button>
  );
}

export function AccountSelector({ accounts, isLoading, onSelect, onConnect, onRefresh }: AccountSelectorProps) {
  const user = useAuthStore((s) => s.user);

  // Empty state
  if (!isLoading && accounts.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 p-4 sm:p-6 md:p-8 text-center">
        <div className="rounded-full bg-primary/10 p-4">
          <Shield className="h-10 w-10 text-primary" />
        </div>
        <h2 className="text-xl font-semibold text-foreground">Connect Your Zoom Account</h2>
        <p className="max-w-md text-sm text-muted-foreground">
          Connect your Zoom account to generate a comprehensive health report covering security,
          user management, meetings, and more.
        </p>
        <button
          onClick={onConnect}
          className="mt-2 rounded-lg bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          Connect with Zoom
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col p-4 sm:p-6 md:p-8">
      <div className="mx-auto w-full max-w-4xl space-y-6">
        {/* Header: user avatar + name + email */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-medium text-primary-foreground">
              {getInitials(user?.name, user?.email)}
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-foreground truncate">
                {user?.name ?? 'User'}
              </h2>
              <p className="text-sm text-muted-foreground truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={onRefresh}
            disabled={isLoading}
            className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground disabled:opacity-50"
            title="Refresh accounts"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Section label */}
        <div>
          <h3 className="text-sm font-medium text-muted-foreground">Your organizations</h3>
        </div>

        {/* Card Grid */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {isLoading ? (
            <>
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
            </>
          ) : (
            accounts.map((account) => (
              <OrgCard key={account.accountId} account={account} onSelect={onSelect} />
            ))
          )}
        </div>

        {/* Connect another account */}
        {!isLoading && (
          <div className="flex justify-center pt-2">
            <button
              onClick={onConnect}
              className="flex items-center gap-1.5 text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
            >
              <Plus className="h-3.5 w-3.5" />
              Connect another account
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
